import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { DEPOSIT_METHODS, RECEPTION_METHODS, CORRIDORS, STATUS_LABEL, formatAmount, formatDate } from "@/lib/constants";
import BrandLogo from "@/components/BrandLogos";
import ImageLightbox from "@/components/ImageLightbox";
import ProofUpload from "@/components/ProofUpload";
import PdfViewerModal from "@/components/PdfViewerModal";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Send as SendIcon, XCircle, Clock, UserCheck, FileImage, X as CloseIcon, ShieldCheck, IdCard, MessageSquare, Upload as UploadIcon, AlertTriangle, FileText, MessageCircle, Copy } from "lucide-react";

const ACTIVE_AGENT_KEY = "sl_active_agent";
const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

const ACTIONS = {
  en_attente: [
    { target: "verifie", label: "Valider le dépôt", icon: CheckCircle2, color: "sl-btn-primary" },
    { target: "annule", label: "Annuler", icon: XCircle, color: "sl-btn-outline" },
  ],
  verifie: [
    { target: "envoye", label: "Marquer comme envoyé au Gabon", icon: SendIcon, color: "sl-btn-primary" },
    { target: "annule", label: "Annuler", icon: XCircle, color: "sl-btn-outline" },
  ],
  envoye: [
    { target: "termine", label: "Confirmer réception bénéficiaire", icon: CheckCircle2, color: "sl-btn-secondary" },
    { target: "annule", label: "Annuler", icon: XCircle, color: "sl-btn-outline" },
  ],
  termine: [],
  annule: [],
};

export default function AdminTransactionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tx, setTx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [agents, setAgents] = useState([]);
  const [activeAgentId, setActiveAgentId] = useState(() => localStorage.getItem(ACTIVE_AGENT_KEY) || "");
  const [lightbox, setLightbox] = useState(null);
  const [payoutProofPath, setPayoutProofPath] = useState("");
  const [savingProof, setSavingProof] = useState(false);
  const [commentDraft, setCommentDraft] = useState("");
  const [postingComment, setPostingComment] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [txRes, agentsRes] = await Promise.all([
        api.get(`/admin/transactions/${id}`),
        api.get("/admin/agents"),
      ]);
      setTx(txRes.data);
      setAgents(agentsRes.data.filter((a) => a.active));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const setActiveAgent = (id) => {
    setActiveAgentId(id);
    if (id) localStorage.setItem(ACTIVE_AGENT_KEY, id);
    else localStorage.removeItem(ACTIVE_AGENT_KEY);
  };

  const doAction = async (target) => {
    const agent = agents.find((a) => a.id === activeAgentId);
    if (!agent && agents.length > 0) {
      toast.error("Sélectionnez d'abord votre identité d'agent en haut de la page.");
      return;
    }
    let reason = null;
    if (target === "annule") {
      reason = window.prompt("Raison de l'annulation ?");
      if (reason === null) return;
    }
    setActing(target);
    try {
      const { data } = await api.post(`/admin/transactions/${id}/status`, {
        status: target,
        reason,
        agent_id: agent?.id || null,
        agent_name: agent?.name || null,
      });
      setTx(data);
      toast.success(`Statut mis à jour : ${STATUS_LABEL[target]}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setActing(null); }
  };

  if (loading || !tx) return <div className="text-gray-500">Chargement…</div>;

  const actions = ACTIONS[tx.status] || [];
  // Latest non-system timeline entry → "Pris en charge par"
  const handler = (tx.timeline || []).filter((t) => t.agent_name).slice(-1)[0];
  // Proof image URL with Bearer token via query string (img tag cannot send headers)
  const token = localStorage.getItem("sl_token");
  const proofUrl = tx.proof_path ? `${API_BASE}/uploads/proof/${tx.code}?auth=${token || ""}` : "";
  const payoutProofUrl = tx.payout_proof_path ? `${API_BASE}/transactions/${tx.code}/payout-proof` : "";
  const needsPayoutProof = tx.status === "envoye" && !tx.payout_proof_path;
  const internalComments = Array.isArray(tx.internal_comments) ? tx.internal_comments : [];

  const submitPayoutProof = async () => {
    if (!payoutProofPath) {
      toast.error("Téléversez d'abord la capture du paiement effectué.");
      return;
    }
    setSavingProof(true);
    try {
      const { data } = await api.post(`/admin/transactions/${tx.id}/payout-proof`, { payout_proof_path: payoutProofPath });
      setTx(data);
      setPayoutProofPath("");
      toast.success("Preuve de paiement enregistrée.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSavingProof(false); }
  };

  const submitComment = async (e) => {
    e?.preventDefault();
    const text = commentDraft.trim();
    if (!text) return;
    setPostingComment(true);
    try {
      await api.post(`/admin/transactions/${tx.id}/comments`, { text });
      setCommentDraft("");
      await load();
      toast.success("Commentaire ajouté.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setPostingComment(false); }
  };

  return (
    <div>
      <Link to="/admin" className="inline-flex items-center gap-2 text-gray-500 hover:text-[var(--sl-primary)] text-sm" data-testid="back-link">
        <ArrowLeft size={16} /> Retour
      </Link>

      <div className="mt-4 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-gray-500">Transaction · {CORRIDORS[tx.corridor || "tg_ga"]?.label}</div>
          <h1 className="font-mono text-3xl font-bold text-[var(--sl-primary)]" data-testid="tx-code">{tx.code}</h1>
          <div className="text-sm text-gray-500 mt-1">Créée le {formatDate(tx.created_at)}</div>
          {handler && (
            <div className="mt-2 inline-flex items-center gap-2 text-xs bg-[var(--sl-bg-alt)] px-3 py-1.5 rounded-full" data-testid="handler-badge">
              <UserCheck size={14} className="text-[var(--sl-primary)]" />
              <span className="text-gray-500">Pris en charge par</span>
              <span className="font-semibold text-gray-900">{handler.agent_name}</span>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusBadge status={tx.status} cancellationReason={tx.cancellation_reason} />
          {agents.length > 0 && actions.length > 0 && (
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500">Je suis :</label>
              <select
                value={activeAgentId}
                onChange={(e) => setActiveAgent(e.target.value)}
                className="text-sm px-3 py-1.5 rounded-lg border bg-white outline-none focus:border-[var(--sl-primary)]"
                style={{ borderColor: "var(--sl-border)" }}
                data-testid="acting-as-select"
              >
                <option value="">— Choisir l'agent —</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* ============ Communication client ============ */}
      <ClientCommunicationCard tx={tx} onOpenPdf={() => setPdfOpen(true)} />

      <div className="mt-6 grid md:grid-cols-3 gap-4">
        <div className="sl-card">
          <div className="text-xs uppercase text-gray-500">Montant envoyé</div>
          <div className="font-heading text-2xl font-bold mt-1">{formatAmount(tx.amount_xof, tx.source_currency || "XOF")}</div>
        </div>
        <div className="sl-card">
          <div className="text-xs uppercase text-gray-500">Frais</div>
          <div className="font-heading text-2xl font-bold mt-1 text-amber-700">{formatAmount(tx.fee, tx.source_currency || "XOF")}</div>
          <div className="text-xs text-gray-500 mt-1">Taux : × {tx.exchange_rate}</div>
        </div>
        <div className="sl-card">
          <div className="text-xs uppercase text-gray-500">Reçu</div>
          <div className="font-heading text-2xl font-bold mt-1 text-[var(--sl-primary)]">{formatAmount(tx.amount_xaf, tx.target_currency || "XAF")}</div>
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <div className="sl-card">
          <h3 className="font-heading font-semibold mb-3">Expéditeur</h3>
          <Row label="Nom" value={tx.sender_name} />
          <Row label="Téléphone" value={tx.sender_phone} />
          {tx.sender_email && <Row label="Email" value={tx.sender_email} />}
          <Row label="Méthode dépôt" value={DEPOSIT_METHODS[tx.deposit_method]?.label} />
          {tx.payment_reference && <Row label="Référence paiement" value={tx.payment_reference} mono />}
          {tx.note && <Row label="Note" value={tx.note} />}
        </div>
        <div className="sl-card">
          <h3 className="font-heading font-semibold mb-3">Bénéficiaire</h3>
          <Row label="Nom" value={tx.beneficiary_name} />
          {tx.beneficiary_phone && <Row label="Téléphone" value={tx.beneficiary_phone} />}
          {tx.beneficiary_email && <Row label="Email" value={tx.beneficiary_email} />}
          {tx.beneficiary_account && <Row label={tx.reception_method === "virement_instantane" ? "IBAN" : "Compte"} value={tx.beneficiary_account} mono />}
          <Row label="Méthode réception" value={RECEPTION_METHODS[tx.reception_method]?.label} />
        </div>
      </div>

      {/* KYC document (only when present, admin-only access) */}
      {tx.kyc_doc_path && (
        <div className="mt-6 sl-card" data-testid="kyc-card">
          <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
            <ShieldCheck size={18} className="text-[var(--sl-primary)]" /> Vérification d'identité (KYC)
          </h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm mb-4">
            <Field label="Type de pièce" value={
              tx.kyc_doc_type === "cni" ? "Carte d'identité (CNI)" :
              tx.kyc_doc_type === "passeport" ? "Passeport" :
              tx.kyc_doc_type === "permis" ? "Permis de conduire" : tx.kyc_doc_type
            } />
            <Field label="Nom complet sur la pièce" value={tx.kyc_full_name} />
            <Field label="Numéro de la pièce" value={tx.kyc_doc_number} mono />
          </div>
          <button
            type="button"
            onClick={() => setLightbox({
              src: `${API_BASE}/admin/transactions/${tx.code}/kyc-doc?auth=${token || ""}`,
              alt: "Document KYC",
              isPdf: (tx.kyc_doc_path || "").toLowerCase().endsWith(".pdf"),
            })}
            className="sl-btn-outline inline-flex"
            data-testid="kyc-doc-open"
          >
            <IdCard size={16} /> Ouvrir le document KYC
          </button>
          <p className="text-xs text-gray-500 mt-3">
            Consultation soumise à audit (action « view_kyc_doc » enregistrée).
          </p>
        </div>
      )}

      {/* Proof of deposit */}
      {proofUrl && (
        <div className="mt-6 sl-card" data-testid="proof-card">
          <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
            <FileImage size={18} className="text-[var(--sl-primary)]" />
            Capture d'écran du dépôt
          </h3>
          <button
            type="button"
            onClick={() => setLightbox({ src: proofUrl, alt: "Capture du dépôt" })}
            className="block group w-full text-left"
            data-testid="proof-image-link"
          >
            <img
              src={proofUrl}
              alt="Capture du dépôt"
              className="max-h-[480px] rounded-xl border bg-white object-contain mx-auto group-hover:opacity-90 transition cursor-zoom-in"
              style={{ borderColor: "var(--sl-border)" }}
            />
            <div className="mt-2 text-xs text-gray-500 text-center">Cliquez pour agrandir</div>
          </button>
        </div>
      )}

      {/* Payout proof — admin uploads the screenshot of the mobile money / PayPal payment to beneficiary.
          Mandatory before the transaction can be marked as 'terminé'. */}
      <div className="mt-6 sl-card" data-testid="payout-proof-card">
        <h3 className="font-heading font-semibold mb-1 flex items-center gap-2">
          <FileImage size={18} className="text-[var(--sl-primary)]" />
          Preuve de paiement au bénéficiaire
          {tx.payout_proof_path ? (
            <span className="ml-2 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">
              <CheckCircle2 size={11} /> Téléversée
            </span>
          ) : (
            <span className="ml-2 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 font-medium">
              <AlertTriangle size={11} /> Manquante
            </span>
          )}
        </h3>
        <p className="text-xs text-gray-500 mb-3">
          {tx.payout_proof_path
            ? "Capture du transfert Mobile Money / PayPal envoyé au bénéficiaire. Obligatoire avant la confirmation finale."
            : "Téléversez la capture du transfert Mobile Money / PayPal envoyé au bénéficiaire. Sans cette preuve, vous ne pourrez pas confirmer la réception."}
        </p>

        {tx.payout_proof_path ? (
          <div>
            <button
              type="button"
              onClick={() => setLightbox({ src: payoutProofUrl, alt: "Preuve de paiement bénéficiaire" })}
              className="block w-full text-left group"
              data-testid="payout-proof-open"
            >
              <img
                src={payoutProofUrl}
                alt="Preuve de paiement"
                className="max-h-[360px] rounded-xl border bg-white object-contain mx-auto group-hover:opacity-90 transition cursor-zoom-in"
                style={{ borderColor: "var(--sl-border)" }}
              />
            </button>
            {tx.payout_proof_uploaded_by && (
              <div className="text-xs text-gray-500 mt-2 text-center">
                Téléversée par <span className="font-medium text-gray-700">{tx.payout_proof_uploaded_by.admin_name || tx.payout_proof_uploaded_by.admin_email}</span>
                {tx.payout_proof_uploaded_at && <> · {formatDate(tx.payout_proof_uploaded_at)}</>}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <ProofUpload value={payoutProofPath} onChange={setPayoutProofPath} data-testid="payout-proof-upload" />
            <button
              type="button"
              onClick={submitPayoutProof}
              disabled={savingProof || !payoutProofPath}
              className="sl-btn-primary"
              data-testid="payout-proof-submit"
            >
              <UploadIcon size={14} /> {savingProof ? "Envoi…" : "Enregistrer la preuve"}
            </button>
          </div>
        )}
      </div>

      {needsPayoutProof && (
        <div className="mt-4 rounded-xl border-2 border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 flex items-start gap-2" data-testid="payout-proof-warning">
          <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">Preuve de paiement obligatoire</div>
            <div className="text-xs">Téléversez la capture du transfert Mobile Money/PayPal effectué au bénéficiaire ci-dessus avant de pouvoir cliquer sur « Confirmer réception bénéficiaire ».</div>
          </div>
        </div>
      )}

      {/* Internal admin comments — never exposed publicly */}
      <div className="mt-6 sl-card" data-testid="internal-comments-card">
        <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
          <MessageSquare size={18} className="text-[var(--sl-primary)]" />
          Commentaires internes <span className="text-xs font-normal text-gray-500">({internalComments.length})</span>
        </h3>
        {internalComments.length === 0 ? (
          <p className="text-xs text-gray-500 mb-3">Aucun commentaire interne. Notez ici toute information utile à l&apos;équipe (problèmes, suivi téléphonique, contexte…).</p>
        ) : (
          <ul className="space-y-3 mb-4">
            {internalComments.map((c) => (
              <li key={c.id} className="rounded-lg bg-[var(--sl-bg-alt)] p-3" data-testid={`comment-${c.id}`}>
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                  <UserCheck size={11} />
                  <span className="font-medium text-gray-700">{c.by_name || c.by_email}</span>
                  <span>·</span>
                  <span>{formatDate(c.at)}</span>
                </div>
                <div className="text-sm text-gray-800 whitespace-pre-wrap">{c.text}</div>
              </li>
            ))}
          </ul>
        )}
        <form onSubmit={submitComment} className="flex gap-2">
          <input
            value={commentDraft}
            onChange={(e) => setCommentDraft(e.target.value)}
            placeholder="Ajouter un commentaire interne…"
            className="sl-input flex-1"
            maxLength={2000}
            data-testid="comment-input"
          />
          <button
            type="submit"
            disabled={postingComment || !commentDraft.trim()}
            className="sl-btn-primary !px-5 !py-2 !text-sm"
            data-testid="comment-submit"
          >
            {postingComment ? "…" : "Ajouter"}
          </button>
        </form>
      </div>

      {actions.length > 0 && (
        <div className="mt-6 sl-card">
          <h3 className="font-heading font-semibold mb-4">Actions disponibles</h3>
          <div className="flex flex-wrap gap-3">
            {actions.map((a) => (
              <button
                key={a.target}
                onClick={() => doAction(a.target)}
                disabled={acting !== null}
                className={a.color}
                data-testid={`action-${a.target}`}
              >
                <a.icon size={16} /> {acting === a.target ? "…" : a.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 sl-card border-2 border-red-100 bg-red-50/30">
        <h3 className="font-heading font-semibold mb-2 text-red-800">Archivage / suppression</h3>
        <p className="text-xs text-gray-600 mb-3">
          Une transaction archivée n&apos;apparaît plus dans le dashboard mais reste consultable dans les archives. La suppression est définitive.
        </p>
        <div className="flex flex-wrap gap-3">
          {tx.archived ? (
            <button
              onClick={async () => {
                if (!window.confirm("Restaurer cette transaction ?")) return;
                try {
                  await api.post(`/admin/transactions/${tx.id}/restore`);
                  toast.success("Transaction restaurée");
                  navigate(0);
                } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
              }}
              className="sl-btn-outline text-sm"
              data-testid="action-restore"
            >
              ♻️ Restaurer
            </button>
          ) : (
            <button
              onClick={async () => {
                if (!window.confirm("Archiver cette transaction ? Elle ne s'affichera plus sur le dashboard.")) return;
                try {
                  await api.post(`/admin/transactions/${tx.id}/archive`);
                  toast.success("Transaction archivée");
                  navigate("/admin");
                } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
              }}
              className="sl-btn-outline text-sm"
              data-testid="action-archive"
            >
              📥 Archiver
            </button>
          )}
          <button
            onClick={async () => {
              if (!window.confirm("⚠️ SUPPRIMER définitivement cette transaction ? Cette action est irréversible.")) return;
              if (!window.confirm("Confirmation finale : tapez OK pour supprimer.")) return;
              try {
                await api.delete(`/admin/transactions/${tx.id}`);
                toast.success("Transaction supprimée");
                navigate("/admin");
              } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
            }}
            className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold border-2 border-red-300 text-red-700 hover:bg-red-100 transition"
            data-testid="action-delete"
          >
            🗑️ Supprimer définitivement
          </button>
        </div>
      </div>

      <div className="mt-6 sl-card">
        <h3 className="font-heading font-semibold mb-4">Chronologie complète</h3>
        <ol className="space-y-3">
          {(tx.timeline || []).map((t, i) => (
            <li key={i} className="flex items-start gap-3" data-testid={`admin-timeline-${i}`}>
              <div className="mt-1 w-7 h-7 rounded-full grid place-items-center bg-[var(--sl-primary)]/10 text-[var(--sl-primary)]">
                {t.status === "termine" ? <CheckCircle2 size={14} /> : <Clock size={14} />}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{STATUS_LABEL[t.status]}</span>
                  {t.agent_name && (
                    <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">
                      <UserCheck size={11} /> {t.agent_name}
                    </span>
                  )}
                  <span className="text-xs text-gray-400">par {t.by}</span>
                </div>
                <div className="text-xs text-gray-500">{formatDate(t.at)}</div>
                {t.reason && <div className="text-xs text-amber-700">Raison : {t.reason}</div>}
              </div>
            </li>
          ))}
        </ol>
      </div>
      {lightbox && (
        <ImageLightbox
          src={lightbox.src}
          alt={lightbox.alt}
          downloadHref={lightbox.src}
          onClose={() => setLightbox(null)}
        />
      )}
      {pdfOpen && (
        <PdfViewerModal
          src={`${API_BASE}/transactions/${tx.code}/receipt`}
          title={`Reçu Swiftlink-Africa · ${tx.code}`}
          downloadName={`recu-${tx.code}.pdf`}
          onClose={() => setPdfOpen(false)}
        />
      )}
    </div>
  );
}

/* ============ Client Communication card ============ */
function ClientCommunicationCard({ tx, onOpenPdf }) {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const receiptUrl = `${BACKEND_URL}/api/transactions/${tx.code}/receipt`;
  const trackUrl = `${BACKEND_URL}/suivi?code=${tx.code}`;

  // The client's WhatsApp number = sender_phone of the transaction (digits only for wa.me).
  const waNumber = (tx.sender_phone || "").replace(/[^\d]/g, "");
  const beneficiaryName = tx.beneficiary_name || "votre bénéficiaire";
  const sender = tx.sender_name || "";

  const amountSent = formatAmount(tx.amount_xof, tx.source_currency || "XOF");
  const amountReceived = formatAmount(tx.amount_xaf, tx.target_currency || "XAF");
  const statusLabel = STATUS_LABEL[tx.status] || tx.status;

  const message = [
    `Bonjour ${sender || ""}`.trim() + ",",
    ``,
    `Voici le reçu officiel de votre transfert Swiftlink-Africa :`,
    `• Code : ${tx.code}`,
    `• Montant envoyé : ${amountSent}`,
    `• Reçu par ${beneficiaryName} : ${amountReceived}`,
    `• Statut : ${statusLabel}`,
    ``,
    `📄 Téléchargez le reçu PDF : ${receiptUrl}`,
    `🔎 Suivi en ligne : ${trackUrl}`,
    ``,
    `Merci d'avoir choisi Swiftlink-Africa !`,
  ].join("\n");

  const waLink = waNumber
    ? `https://wa.me/${waNumber}?text=${encodeURIComponent(message)}`
    : `https://wa.me/?text=${encodeURIComponent(message)}`;

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast.success("Message copié dans le presse-papier");
    } catch (_e) { toast.error("Impossible de copier"); }
  };

  const copyReceiptUrl = async () => {
    try {
      await navigator.clipboard.writeText(receiptUrl);
      toast.success("Lien du reçu copié");
    } catch (_e) { toast.error("Impossible de copier"); }
  };

  return (
    <div className="mt-6 sl-card" data-testid="client-communication-card">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-8 h-8 rounded-full bg-[var(--sl-primary)]/10 text-[var(--sl-primary)] grid place-items-center">
          <SendIcon size={16} />
        </span>
        <h3 className="font-heading font-semibold">Transmettre au client</h3>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Une fois la transaction validée, partagez le reçu au client par WhatsApp ou en l&apos;envoyant manuellement.
        {!waNumber && <span className="block text-amber-700 mt-1">⚠ Aucun numéro client enregistré — WhatsApp s&apos;ouvrira sans destinataire pré-rempli.</span>}
      </p>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onOpenPdf}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--sl-primary)]/10 text-[var(--sl-primary)] hover:bg-[var(--sl-primary)]/15 font-medium text-sm transition"
          data-testid="admin-view-receipt"
          title="Visualiser le reçu PDF"
        >
          <FileText size={14} /> Voir le reçu PDF
        </button>

        <a
          href={receiptUrl}
          download={`recu-${tx.code}.pdf`}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 font-medium text-sm transition"
          data-testid="admin-download-receipt"
          title="Télécharger le reçu PDF"
        >
          <UploadIcon size={14} className="rotate-180" /> Télécharger
        </a>

        <a
          href={waLink}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#25D366] text-white hover:bg-[#1ebe57] font-semibold text-sm transition shadow"
          data-testid="admin-send-whatsapp"
          title={waNumber ? `Ouvrir WhatsApp pour ${tx.sender_phone}` : "Ouvrir WhatsApp (sélectionner un destinataire)"}
        >
          <MessageCircle size={14} /> Envoyer sur WhatsApp
        </a>

        <button
          type="button"
          onClick={copyMessage}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white text-gray-700 border hover:bg-gray-50 text-xs font-medium transition"
          style={{ borderColor: "var(--sl-border)" }}
          data-testid="admin-copy-message"
        >
          <Copy size={12} /> Copier le message
        </button>

        <button
          type="button"
          onClick={copyReceiptUrl}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white text-gray-700 border hover:bg-gray-50 text-xs font-medium transition"
          style={{ borderColor: "var(--sl-border)" }}
          data-testid="admin-copy-link"
        >
          <Copy size={12} /> Copier le lien
        </button>
      </div>

      <details className="mt-4 group" data-testid="message-preview-details">
        <summary className="cursor-pointer text-xs font-medium text-gray-600 select-none hover:text-[var(--sl-primary)]">
          Aperçu du message client
        </summary>
        <pre className="mt-2 whitespace-pre-wrap text-xs bg-[var(--sl-bg-alt)] text-gray-800 p-3 rounded-lg font-mono border" style={{ borderColor: "var(--sl-border)" }} data-testid="message-preview">
{message}
        </pre>
      </details>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 text-sm border-b border-gray-50 last:border-0">
      <span className="text-gray-500">{label}</span>
      <span className={`text-gray-900 text-right ${mono ? "font-mono" : ""}`}>{value || "—"}</span>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-gray-500">{label}</div>
      <div className={`text-sm text-gray-900 mt-0.5 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}
